/**
 * Imports service — Import Data page (org structure + members) and undo.
 * Mirrors the axios contract used elsewhere: apiClient unwraps json_success to
 * the inner `data`, so `const { data } = await apiClient.post<T>(...)` yields T.
 */

import apiClient from '../lib/axios';

// ---- Shared row-level result shapes ----
export interface RowError {
  row: number;
  field: string;
  message: string;
}
export interface RowWarning {
  row: number;
  message: string;
}

// ---- Upload ----
export interface ImportUploadResponse {
  upload_id: string;
  csv: { filename: string; size: number };
  zip: { filename: string; size: number } | null;
}

// ---- Org structure ----
export interface OrgValidationResponse {
  upload_id: string;
  total_rows: number;
  error_rows: number;
  errors: RowError[];
  warnings: RowWarning[];
  can_import: boolean;
  status: 'valid' | 'has_errors';
  /** Set when the file was not UTF-8 and was converted (TW-395). */
  encoding_notice?: string | null;
}
export interface OrgImportResponse {
  upload_id: string;
  import_batch_id: number;
  departments_created: number;
  teams_created: number;
  teams_reparented: number;
  logos_applied: number;
  skipped: number;
  warnings: RowWarning[];
  status: 'completed';
}

// ---- Members ----
export interface CsvRow {
  row_number: number;
  data: Record<string, string>;
}
export interface MemberValidationResponse {
  upload_id: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  errors: RowError[];
  warnings: RowWarning[];
  rows: CsvRow[];
  can_import: boolean;
  status: 'valid' | 'has_errors';
  /** Set when the file was not UTF-8 and was converted (TW-395). */
  encoding_notice?: string | null;
}
export interface ImportBatch {
  id: number;
  type: 'org_structure' | 'members';
  imported_count: number;
  updated_count: number;
  skipped_count: number;
  reversed_at: string | null;
  created_at: string;
  undoable: boolean;
  undo_reason: string;
  /** Who ran the import / who undid it; null when unknown (TW-261). */
  created_by_name?: string | null;
  reversed_by_name?: string | null;
}
export interface MemberImportResponse {
  upload_id: string;
  report_id: string;
  import_batch_id: number;
  total_rows: number;
  imported: number;
  skipped: number;
  failed: number;
  report_url: string;
  status: 'completed';
}
/** A corrected row posted to execute-from-payload (fix panel). */
export interface CorrectedRow {
  row_number: number;
  data: Record<string, string>;
}

export interface UndoAvailability {
  undoable: boolean;
  reason: string;
}
export interface MissingJoinDate {
  count: number;
  members: Array<{ id: number; first_name: string; last_name: string }>;
}
export interface BatchMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  department_name: string | null;
  team_name: string | null;
}
export interface BatchMembersResponse {
  reversed: boolean;
  members: BatchMember[];
}

function multipart(csv: File, zip: File | null): FormData {
  const fd = new FormData();
  fd.append('csv', csv);
  if (zip) fd.append('zip', zip);
  return fd;
}

const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const importsService = {
  // ===== Organization structure =====
  uploadOrg: async (csv: File, zip: File | null): Promise<ImportUploadResponse> => {
    const { data } = await apiClient.post<ImportUploadResponse>('/org_imports/import/upload', multipart(csv, zip), cfg);
    return data;
  },
  validateOrg: async (uploadId: string): Promise<OrgValidationResponse> => {
    const { data } = await apiClient.post<OrgValidationResponse>('/org_imports/import/validate', { upload_id: uploadId });
    return data;
  },
  executeOrg: async (uploadId: string): Promise<OrgImportResponse> => {
    const { data } = await apiClient.post<OrgImportResponse>('/org_imports/import/execute', { upload_id: uploadId });
    return data;
  },
  downloadOrgTemplate: async (): Promise<void> => {
    const res = await apiClient.get('/org_imports/import/template', { responseType: 'blob' });
    downloadBlob(res.data, 'org_structure_template.csv');
  },
  downloadCurrentStructure: async (): Promise<void> => {
    const res = await apiClient.get('/org_imports/download_current', { responseType: 'blob' });
    downloadBlob(res.data, 'org_structure_current.csv');
  },

  // ===== Members =====
  uploadMembers: async (csv: File, zip: File | null): Promise<ImportUploadResponse> => {
    const { data } = await apiClient.post<ImportUploadResponse>('/member_imports/import/upload', multipart(csv, zip), cfg);
    return data;
  },
  validateMembers: async (uploadId: string): Promise<MemberValidationResponse> => {
    const { data } = await apiClient.post<MemberValidationResponse>('/member_imports/import/validate', { upload_id: uploadId });
    return data;
  },
  /** Execute; pass `rows` to import corrected rows from the fix panel (execute-from-payload). */
  executeMembers: async (uploadId: string, rows?: CorrectedRow[]): Promise<MemberImportResponse> => {
    const body: Record<string, unknown> = { upload_id: uploadId };
    if (rows) body.rows = rows;
    const { data } = await apiClient.post<MemberImportResponse>('/member_imports/import/execute', body);
    return data;
  },
  downloadMemberTemplate: async (): Promise<void> => {
    const res = await apiClient.get('/member_imports/import/template', { responseType: 'blob' });
    downloadBlob(res.data, 'members_template.csv');
  },
  downloadReport: async (reportId: string): Promise<void> => {
    const res = await apiClient.get(`/member_imports/import/report/${reportId}`, { responseType: 'blob' });
    downloadBlob(res.data, `import_report_${reportId}.csv`);
  },

  // ===== Dashboard widget =====
  missingJoinDate: async (): Promise<MissingJoinDate> => {
    const { data } = await apiClient.get<MissingJoinDate>('/members_missing_join_date');
    return data;
  },

  // ===== Imports history =====
  listBatches: async (): Promise<ImportBatch[]> => {
    const { data } = await apiClient.get<{ batches: ImportBatch[] }>('/member_imports/import/batches');
    return data.batches;
  },
  /** Members this batch created (empty + reversed:true once undone). */
  batchMembers: async (batchId: number): Promise<BatchMembersResponse> => {
    const { data } = await apiClient.get<BatchMembersResponse>(`/member_imports/import/batch_members/${batchId}`);
    return data;
  },

  // ===== Undo (member imports only) =====
  undoAvailability: async (batchId: number): Promise<UndoAvailability> => {
    const { data } = await apiClient.get<UndoAvailability>(`/member_imports/import/undo/${batchId}`);
    return data;
  },
  undo: async (batchId: number): Promise<{ deleted: number }> => {
    const { data } = await apiClient.post<{ deleted: number }>('/member_imports/import/undo', { import_batch_id: batchId });
    return data;
  },
};
